/**
 * Page Designer search content.
 *
 * CARD is the layout container. SEARCH is a Content Function inside the card:
 * Card → Content → Add Content → Search.
 *
 * The set of tables a Search card may use, the fields of each table that may
 * be searched, and the query that runs are all owned by the server registry
 * (`/api/page-designer/data-sources`). This module holds only the saved card
 * configuration and the shape-level sanitizing of it:
 *
 *   { sourceId, fields: [...], title, maxResults }
 *
 * There is deliberately no table list and no column list here. A table added
 * to EMRbyMD becomes available to Search when the server registry advertises
 * it — this file does not change.
 */

import { fetchSearchDataSources, runSearchQuery } from "@/lib/pageDesignerSearchApi";

export const CONTENT_TYPE_SEARCH = "search";
export const SEARCH_PREFERRED_WIDTH = 18;
export const SEARCH_PREFERRED_HEIGHT = 14;
export const SEARCH_MAX_RESULTS_DEFAULT = 10;
export const SEARCH_MAX_RESULTS_LIMIT = 50;
export const SEARCH_FIELD_LIMIT = 12;

/** Source and field ids are server-issued slugs. Anything else is not an id. */
const ID_RE = /^[a-z0-9][a-zA-Z0-9-]{0,63}$/;

function asInt(value, fallback) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

function sanitizeId(value) {
  const text = typeof value === "string" ? value.trim() : "";
  return ID_RE.test(text) ? text : "";
}

function sanitizeTitle(value) {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, 120);
}

export function emptySearchContent() {
  return {
    sourceId: "",
    fields: [],
    title: "",
    maxResults: SEARCH_MAX_RESULTS_DEFAULT,
  };
}

/**
 * Structural sanitizing only. Whether a source or field actually exists is the
 * server's answer, not this module's: a card saved against a source that has
 * since changed is rejected by the server at search time, never silently
 * pointed at different data.
 */
export function sanitizeSearchContent(raw) {
  const empty = emptySearchContent();
  if (!raw || typeof raw !== "object") return empty;
  const sourceId = sanitizeId(raw.sourceId);
  const fields = [];
  if (sourceId) {
    (Array.isArray(raw.fields) ? raw.fields : []).forEach((value) => {
      const id = sanitizeId(value);
      if (!id || fields.includes(id) || fields.length >= SEARCH_FIELD_LIMIT) return;
      fields.push(id);
    });
  }
  return {
    sourceId,
    fields,
    title: sanitizeTitle(raw.title),
    maxResults: Math.min(
      SEARCH_MAX_RESULTS_LIMIT,
      Math.max(1, asInt(raw.maxResults, SEARCH_MAX_RESULTS_DEFAULT)),
    ),
  };
}

export function searchContentEquals(left, right) {
  const a = sanitizeSearchContent(left);
  const b = sanitizeSearchContent(right);
  return (
    a.sourceId === b.sourceId &&
    a.title === b.title &&
    a.maxResults === b.maxResults &&
    a.fields.length === b.fields.length &&
    a.fields.every((id, index) => id === b.fields[index])
  );
}

export function isSearchCard(card) {
  return (card?.contentType || "") === CONTENT_TYPE_SEARCH;
}

export function isSearchConfigured(content) {
  const config = sanitizeSearchContent(content);
  return Boolean(config.sourceId && config.fields.length);
}

export function addSearchField(content, fieldId) {
  const next = sanitizeSearchContent(content);
  const id = sanitizeId(fieldId);
  if (!id || !next.sourceId || next.fields.includes(id)) return next;
  return sanitizeSearchContent({ ...next, fields: [...next.fields, id] });
}

export function removeSearchField(content, fieldId) {
  const next = sanitizeSearchContent(content);
  return sanitizeSearchContent({
    ...next,
    fields: next.fields.filter((id) => id !== fieldId),
  });
}

export function moveSearchField(content, fieldId, direction) {
  const next = sanitizeSearchContent(content);
  const index = next.fields.indexOf(fieldId);
  if (index < 0) return next;
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= next.fields.length) return next;
  const fields = [...next.fields];
  const [moved] = fields.splice(index, 1);
  fields.splice(target, 0, moved);
  return sanitizeSearchContent({ ...next, fields });
}

/** Server field descriptors ({ id, label }) not yet selected, in server order. */
export function availableSearchFields(content, sourceFields) {
  const next = sanitizeSearchContent(content);
  const selected = new Set(next.fields);
  return (sourceFields || []).filter((field) => !selected.has(field.id));
}

/** Selected fields in the designer's chosen order, resolved against the server list. */
export function selectedSearchFields(content, sourceFields) {
  const next = sanitizeSearchContent(content);
  const byId = new Map((sourceFields || []).map((field) => [field.id, field]));
  return next.fields.map((id) => byId.get(id) || { id, label: id }).filter(Boolean);
}

export function defaultSearchTitle(source) {
  return source?.label ? `Search ${source.label}` : "";
}

export function defaultSearchFields(source) {
  const advertised = new Set((source?.fields || []).map((field) => field.id));
  const defaults = (source?.defaultFields || []).filter((id) => advertised.has(id));
  if (defaults.length) return defaults.slice(0, SEARCH_FIELD_LIMIT);
  return (source?.fields || []).slice(0, 1).map((field) => field.id);
}

/**
 * Available data sources, straight from the server. An unreachable or
 * unauthorized server yields an empty list rather than a guessed one.
 */
export async function loadSearchDataSources() {
  try {
    const data = await fetchSearchDataSources();
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      sources: items.filter((item) => item && typeof item.id === "string"),
      maxResultsLimit: asInt(data?.maxResultsLimit, SEARCH_MAX_RESULTS_LIMIT),
      status: "ready",
    };
  } catch {
    return { sources: [], maxResultsLimit: SEARCH_MAX_RESULTS_LIMIT, status: "error" };
  }
}

export function searchSource(sources, sourceId) {
  const id = sanitizeId(sourceId);
  return (sources || []).find((source) => source.id === id) || null;
}

/**
 * Runs one search. Statuses mirror the Table content function so the runtime
 * component can render without knowing about transport:
 *   idle          — nothing configured yet
 *   empty-query   — configured, but the user has not typed anything, so no
 *                   request is made and no table is read
 *   ready | error
 */
export async function loadSearchResults(content, query) {
  const config = sanitizeSearchContent(content);
  if (!isSearchConfigured(config)) {
    return { items: [], status: "idle", total: 0 };
  }
  const term = typeof query === "string" ? query.trim() : "";
  if (!term) {
    return { items: [], status: "empty-query", total: 0 };
  }
  try {
    const data = await runSearchQuery({
      sourceId: config.sourceId,
      fields: config.fields,
      query: term,
      maxResults: config.maxResults,
    });
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items: items.slice(0, config.maxResults),
      total: asInt(data?.total, items.length),
      fields: Array.isArray(data?.fields) ? data.fields : [],
      status: "ready",
    };
  } catch {
    return { items: [], status: "error", total: 0 };
  }
}
